import moment from 'moment'
import { MouseEvent, useEffect, useRef, useState } from 'react'
import ReactApexChart from 'react-apexcharts'

import { tooltipForConsuptionChart } from 'components/group/utils/tooltipsForCharts'
import { COLORS } from 'styles/colors'
import { localizationApexCharts } from 'utils'
import 'components/group/styles/module.ApexChartTooltip.css'
import { getDynamicColumnWidth } from 'components/group/consuption/helper/getDynamicColumnWidth'

const squarePath = path => {
	const box = path.getBBox()
	if (box.width < 2 || box.height < 1.5) return
	const { x, y, width, height } = box
	path.setAttribute(
		'd',
		`M ${x} ${y} L ${x} ${y + height} L ${x + width} ${y + height} L ${x + width} ${y} Z`,
	)
}

const roundTopPath = (path, radius) => {
	const box = path.getBBox()
	if (box.width < 2 || box.height < 1.5) return
	const r = Math.min(radius, box.width / 2, box.height)
	const { x, y, width, height } = box
	path.setAttribute(
		'd',
		`M ${x} ${y + height} L ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} L ${
			x + width - r
		} ${y} Q ${x + width} ${y} ${x + width} ${y + r} L ${x + width} ${
			y + height
		} Z`,
	)
}

const shapeStackedColumns = (root, radius) => {
	if (!root) return
	const groups = [...root.querySelectorAll('.apexcharts-series')].filter(
		group => group.querySelector('.apexcharts-bar-area'),
	)
	if (!groups.length) return
	const count = Math.max(
		...groups.map(
			group => group.querySelectorAll('.apexcharts-bar-area').length,
		),
	)
	for (let index = 0; index < count; index += 1) {
		const column = groups
			.map(group => ({
				name: group.getAttribute('seriesName') || '',
				path: group.querySelectorAll('.apexcharts-bar-area')[index],
			}))
			.filter(item => item.path && item.path.getBBox().height > 1.5)
		const dataBars = column.filter(item => !item.name.startsWith('Нет'))
		const targets = dataBars.length ? dataBars : column
		targets.sort(
			(left, right) => left.path.getBBox().y - right.path.getBBox().y,
		)
		targets.forEach((item, itemIndex) => {
			if (itemIndex === 0) roundTopPath(item.path, radius)
			else squarePath(item.path)
		})
	}
}

const FeedingApexConsumption = props => {
	const fetchData = props.fetchData ?? []
	const [cursorPosition, setCursorPosition] = useState(null)
	const tipRef = useRef(null)
	const chartId = props.chartId || 'consuptionChart'
	useEffect(() => {
		if (!props.manualTooltip) return
		const onMove = (event: PointerEvent) => {
			const tip = tipRef.current
			const root = document.getElementById(chartId)
			if (!tip || tip.style.display === 'none' || !root) return
			const grid = root.querySelector('.apexcharts-grid')
			const box = (grid ?? root).getBoundingClientRect()
			const inside =
				event.clientX >= box.left &&
				event.clientX <= box.right &&
				event.clientY >= box.top &&
				event.clientY <= box.bottom
			if (!inside) tip.style.display = 'none'
		}
		window.addEventListener('pointermove', onMove)
		return () => window.removeEventListener('pointermove', onMove)
	}, [chartId, props.manualTooltip])
	const psvValuesForTooltip = fetchData.map(el => (el.psv ? el.psv : 0))
	const headQtyValuesForTooltip = fetchData.map(el =>
		el.cattle_count ? el.cattle_count : 0,
	)
	const distributedValuesForTooltip = fetchData.map(el => el.distributed_sum)
	const cleanedValuesForTooltip = fetchData.map(el => el.cleaned_sum)
	const distributedValuesForRenderInChart = fetchData.map(el => {
		if (el.distributed_sum !== null && el.cleaned_sum !== null) {
			return el.distributed_sum > el.cleaned_sum
				? el.distributed_sum - el.cleaned_sum
				: el.distributed_sum
		} else {
			return el.distributed_sum
		}
	})

	const cleanedValuesForRenderInChart = fetchData.map(el => {
		if (el.distributed_sum !== null && el.cleaned_sum !== null) {
			return el.cleaned_sum > el.distributed_sum
				? el.cleaned_sum - el.distributed_sum
				: el.cleaned_sum
		} else {
			return el.cleaned_sum
		}
	})
	const dataXasis = fetchData.map(el => el.date)
	let maxSeriesValue = -Infinity

	for (const data of fetchData) {
		const distributedSum =
			data.distributed_sum !== null ? data.distributed_sum : 0
		const cleanedSum = data.cleaned_sum !== null ? data.cleaned_sum : 0
		const sum =
			distributedSum > cleanedSum
				? Math.round(distributedSum)
				: Math.round(cleanedSum)

		if (sum > maxSeriesValue) {
			maxSeriesValue = sum
		}
	}

	const yearsValues = []
	const sortedData = [...fetchData].sort(
		(a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
	)
	if (sortedData.length === 0) {
		return null
	}
	// Берем первую и последнюю дату из данных
	const firstDate = moment(sortedData[0].date).startOf('month')
	const lastDate = moment(sortedData[sortedData.length - 1].date).endOf('month')
	// Вычисляем количество месяцев в диапазоне
	const monthsCount = lastDate.diff(firstDate, 'months') + 1

	for (let i = 0; i < monthsCount; i++) {
		const startDate = firstDate.clone().add(i, 'months').startOf('month')
		const endDate = firstDate.clone().add(i, 'months').endOf('month')
		const filteredData = sortedData.filter(item =>
			moment(item.date).isBetween(startDate, endDate, null, '[]'),
		)
		const distributed_sum = filteredData.reduce(
			(sum, item) => sum + (item.distributed_sum || 0),
			0,
		)
		const cleaned_sum = filteredData.reduce(
			(sum, item) => sum + (item.cleaned_sum || 0),
			0,
		)
		const averagePSV =
			filteredData.reduce((sum, item) => sum + (item.psv || 0), 0) /
			filteredData.length
		const averageHeadQty =
			filteredData.reduce((sum, item) => sum + (item.cattle_count || 0), 0) /
			filteredData.length
		const monthData = {
			date: startDate.valueOf(),
			distributed_sum,
			cleaned_sum,
			averagePSV,
			averageHeadQty,
		}
		yearsValues.push(monthData)
	}
	yearsValues.sort((a, b) => a.date - b.date)

	const yearsDistributedValues = yearsValues?.map(el => {
		if (el.distributed_sum !== null && el.cleaned_sum !== null) {
			return el.distributed_sum > el.cleaned_sum
				? el.distributed_sum - el.cleaned_sum
				: el.distributed_sum
		} else {
			return el.distributed_sum
		}
	})
	const yearsCleanedValues = yearsValues?.map(el => {
		if (el.distributed_sum !== null && el.cleaned_sum !== null) {
			return el.cleaned_sum > el.distributed_sum
				? el.cleaned_sum - el.distributed_sum
				: el.cleaned_sum
		} else {
			return el.cleaned_sum
		}
	})
	const yearsAveragePSV = yearsValues?.map(el => el.averagePSV)
	const yearsAverageHeadQty = yearsValues?.map(el => el.averageHeadQty)
	const dataXYearsValues = yearsValues?.map(el => new Date(el.date).getTime())

	let maxYearsSeriesValue = -Infinity

	for (const data of yearsValues) {
		const distributedSum =
			data.distributed_sum !== null ? data.distributed_sum : 0
		const cleanedSum = data.cleaned_sum !== null ? data.cleaned_sum : 0

		const sum =
			distributedSum > cleanedSum
				? Math.round(distributedSum)
				: Math.round(cleanedSum)

		if (sum > maxYearsSeriesValue) {
			maxYearsSeriesValue = sum
		}
	}

	const padThreeDays = props.daysBetweenDates === 2 && !props.chartId
	const chartCategories = padThreeDays ? ['', ...dataXasis, ''] : dataXasis
	const distributedValuesWithPadding = padThreeDays
		? [null, ...distributedValuesForRenderInChart, null]
		: distributedValuesForRenderInChart

	const cleanedValuesWithPadding = padThreeDays
		? [null, ...cleanedValuesForRenderInChart, null]
		: cleanedValuesForRenderInChart

	const series = [
		{
			name: 'Остаток',
			color: COLORS.error,
			data:
				props.daysBetweenDates > 90
					? yearsCleanedValues
					: cleanedValuesWithPadding,
		},
		{
			name: 'Роздано корма',
			color: COLORS.main,
			data:
				props.daysBetweenDates > 90
					? yearsDistributedValues
					: distributedValuesWithPadding,
		},
	]

	if (
		distributedValuesForTooltip.some(value => value === null) &&
		cleanedValuesForTooltip.some(value => value === null) &&
		props.daysBetweenDates < 90
	) {
		series.unshift({
			name: 'Нет данных',
			color: COLORS.inputBorder,
			data: fetchData.map(el => {
				if (el.distributed_sum === null && el.cleaned_sum === null) {
					return maxSeriesValue
				}
				return 0
			}),
		})
	}

	const options: any = {
		colors: [COLORS.main, COLORS.inputBorder, COLORS.error],
		mask: false,
		chart: {
			animations: {
				enabled: false,
			},
			zoom: {
				enabled: false,
			},
			stacked: true,
			locales: localizationApexCharts(),
			defaultLocale: 'ru',

			height: 250,
			type: 'bar',
			toolbar: {
				show: false,
			},
			events: props.topRadiusOnly
				? {
						mounted: chart =>
							shapeStackedColumns(
								chart.el || chart.w?.globals?.dom?.baseEl,
								props.borderRadius ?? 8,
							),
						updated: chart =>
							shapeStackedColumns(
								chart.el || chart.w?.globals?.dom?.baseEl,
								props.borderRadius ?? 8,
							),
					}
				: undefined,
		},
		title: {
			style: {
				fontSize: '12px',
				fontFamily: 'Montserrat',
				fontStyle: 'normal',
				fontWeight: 400,
				lineHeight: '15px',
				color: '#7F7F84',
			},
			text: 'Вес, кг',
		},

		dataLabels: {
			enabled: false,
		},
		grid: {
			padding: {
				left: 0,
				right: 0,
			},
			strokeDashArray: [6, 3],
			show: true,
			position: 'back',
			xaxis: {
				lines: {
					show: true,
				},
			},
		},

		plotOptions: {
			bar: {
				horizontal: false,
				borderRadius:
					props.borderRadius ??
					(props.daysBetweenDates <= 1
						? 20
						: props.daysBetweenDates <= 60
							? 8
							: props.daysBetweenDates <= 91
								? 4
								: 8),
				borderRadiusApplication: 'end',
				borderRadiusWhenStacked: 'last',
				columnWidth:
					props.columnWidth ??
					getDynamicColumnWidth(
						props.daysBetweenDates > 90 ? dataXYearsValues : chartCategories,
					),

				dataLabels: {
					hideOverflowingLabels: true,
					total: {
						offsetX: 200,
						enabled: false,
						style: {
							fontSize: '13px',
							fontWeight: 900,
						},
					},
				},
			},
		},
		fill: {
			opacity: 1,
		},
		xaxis: {
			crosshairs: {
				show: false,
			},
			type: 'category',
			tickPlacement: 'between',
			tickAmount: 31,
			tooltip: {
				enabled: false,
			},
			labels: {
				rotate: 0,
				offsetX: padThreeDays ? 50 : 0,
				trim: false,
				hideOverlappingLabels: false,
				formatter: function (value: string) {
					if (!value) {
						return ''
					}

					return moment(value).format('DD.MM')
				},

				datetimeUTC: false,
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
			},
			categories:
				props.daysBetweenDates > 90 ? dataXYearsValues : chartCategories,
		},
		yaxis: {
			crosshairs: {
				show: false,
			},
			tickAmount: 4,
			min: 0,
			max:
				(props.daysBetweenDates > 90 ? maxYearsSeriesValue : maxSeriesValue) +
				(props.yAxisGap ? 0 : 1),
			labels: {
				offsetX: props.yAxisGap ? -10 : 0,
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				formatter: function (value: number, index: number) {
					if (!props.yAxisGap && index % 2 !== 0) {
						return ''
					}
					return Math.round(value).toLocaleString('ru-RU')
				},
			},
		},
		states: {
			normal: {
				filter: {
					type: 'none',
					value: 0,
				},
			},
			hover: {
				filter: {
					type: 'none',
				},
			},
			active: {
				allowMultipleDataPointsSelection: false,
				filter: {
					type: 'none',
					value: 0,
				},
			},
		},
		tooltip: {
			enabled: !props.manualTooltip,
			custom: ({ series, dataPointIndex }) => {
				return tooltipForConsuptionChart(
					series,
					dataPointIndex,
					props.daysBetweenDates,
					distributedValuesForTooltip,
					cleanedValuesForTooltip,
					psvValuesForTooltip,
					headQtyValuesForTooltip,
					yearsDistributedValues,
					yearsCleanedValues,
					yearsAveragePSV,
					yearsAverageHeadQty,
					dataXasis,
				)
			},
		},
		legend: {
			show: false,
		},
	}
	const hideTip = () => {
		if (tipRef.current) tipRef.current.style.display = 'none'
	}
	const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
		if (props.manualTooltip && tipRef.current) {
			const root = e.currentTarget
			const tip = tipRef.current
			const grid = root.querySelector('.apexcharts-grid')
			const seriesGroups = Array.from(
				root.querySelectorAll<SVGElement>(
					'.apexcharts-bar-series .apexcharts-series',
				),
			)
			const pointCount = seriesGroups[0]
				? seriesGroups[0].querySelectorAll('.apexcharts-bar-area').length
				: 0
			if (!grid || pointCount < 1) {
				hideTip()
				return
			}
			const gridBox = grid.getBoundingClientRect()
			const overPlot =
				e.clientX >= gridBox.left &&
				e.clientX <= gridBox.right &&
				e.clientY >= gridBox.top &&
				e.clientY <= gridBox.bottom
			if (!overPlot) {
				hideTip()
				return
			}
			const hoveredBar = seriesGroups
				.flatMap(group =>
					Array.from(
						group.querySelectorAll<SVGElement>('.apexcharts-bar-area'),
						(path, index) => ({ path, index }),
					),
				)
				.find(({ path }) => {
					const box = path.getBoundingClientRect()
					return (
						e.clientX >= box.left &&
						e.clientX <= box.right &&
						e.clientY >= box.top &&
						e.clientY <= box.bottom &&
						box.width > 0 &&
						box.height > 1.5
					)
				})
			if (!hoveredBar) {
				hideTip()
				return
			}
			const slot = hoveredBar.index
			const padded = padThreeDays
			const monthly = props.daysBetweenDates > 90
			const dataIndex = padded ? slot - 1 : slot
			const limit = monthly ? yearsValues.length : fetchData.length
			if (dataIndex < 0 || dataIndex >= limit) {
				hideTip()
				return
			}
			const missing =
				!monthly &&
				distributedValuesForTooltip[dataIndex] == null &&
				cleanedValuesForTooltip[dataIndex] == null
			const seriesForTip = missing
				? [
						distributedValuesForTooltip.map((value, point) =>
							value == null && cleanedValuesForTooltip[point] == null ? 1 : 0,
						),
						distributedValuesForTooltip,
						cleanedValuesForTooltip,
					]
				: [distributedValuesForTooltip, cleanedValuesForTooltip]
			const html = tooltipForConsuptionChart(
				seriesForTip,
				dataIndex,
				props.daysBetweenDates,
				distributedValuesForTooltip,
				cleanedValuesForTooltip,
				psvValuesForTooltip,
				headQtyValuesForTooltip,
				yearsDistributedValues,
				yearsCleanedValues,
				yearsAveragePSV,
				yearsAverageHeadQty,
				dataXasis,
			)
			tip.style.display = 'block'
			if (tip.dataset.html !== html) {
				tip.innerHTML = html
				tip.dataset.html = html
			}
			const rootBox = root.getBoundingClientRect()
			const column = seriesGroups
				.map(group => group.querySelectorAll('.apexcharts-bar-area')[slot])
				.filter((path): path is SVGElement => Boolean(path))
				.map(path => path.getBoundingClientRect())
				.filter(box => box.height > 1.5)
			const anchor = column.length
				? column.reduce((highest, box) =>
						box.top < highest.top ? box : highest,
					)
				: null
			const center = anchor
				? anchor.left + anchor.width / 2 - rootBox.left
				: gridBox.left -
					rootBox.left +
					((slot + 0.5) * gridBox.width) / pointCount
			let left = center - tip.offsetWidth / 2
			const top = (anchor ? anchor.top - rootBox.top : 0) - tip.offsetHeight - 8
			if (left < 8) left = 8
			const maxLeft = rootBox.width - tip.offsetWidth - 8
			if (left > maxLeft) left = Math.max(8, maxLeft)
			tip.style.left = `${left}px`
			tip.style.top = `${top}px`
		}
		if (props.daysBetweenDates === 1) {
			const chartElement = document.getElementById(chartId)
			if (chartElement) {
				const chartRect = chartElement.getBoundingClientRect()
				const mouseX = e.clientX - chartRect.left
				const chartWidth = chartRect.width

				if (mouseX <= chartWidth / 2) {
					setCursorPosition('left')
				} else {
					setCursorPosition('right')
				}
			}
		}
	}
	return (
		<div id={'#chart'}>
			<div
				style={{
					position: 'relative',
					marginLeft: props.yAxisGap ? 0 : '-8px',
				}}
				id={chartId}
				onMouseMove={handleMouseMove}
				onMouseLeave={hideTip}
			>
				<ReactApexChart
					key={`chart-${props.daysBetweenDates}-${props.yAxisGap ? 1 : 0}`}
					id={'apex-chart'}
					options={options}
					series={series}
					type='bar'
					height={props.height ?? 250}
					className={'apex-chart'}
				/>
				{props.manualTooltip ? (
					<div
						ref={tipRef}
						style={{
							display: 'none',
							position: 'absolute',
							zIndex: 20,
							pointerEvents: 'none',
						}}
					/>
				) : null}
			</div>
		</div>
	)
}

export default FeedingApexConsumption
